// Best-effort offline cache: persists selected react-query data (threads, messages, mailboxes, me)
// to IndexedDB (falling back to localStorage) so cached mail stays readable offline.
import { dehydrate, hydrate, type QueryClient } from '@tanstack/react-query';

const DB_NAME = 'mailpilot-cache';
const STORE = 'kv';
// Bump when cached shapes change (v2: /api/auth/me gained workspace) so old copies are ignored.
const KEY = 'rq-v2';
const PERSISTED_ROOTS = new Set(['me', 'mailboxes', 'folders', 'threads', 'thread', 'signatures', 'attention', 'drafts']);

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (!('indexedDB' in window)) return resolve(null);
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbGet(key: string): Promise<string | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readonly');
      const r = tx.objectStore(STORE).get(key);
      r.onsuccess = () => resolve((r.result as string | undefined) ?? null);
      r.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbSet(key: string, value: string | null): Promise<boolean> {
  const db = await openDb();
  if (!db) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      if (value === null) tx.objectStore(STORE).delete(key);
      else tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

export async function restoreCache(qc: QueryClient): Promise<void> {
  try {
    let raw = await idbGet(KEY);
    if (!raw) {
      try {
        raw = localStorage.getItem(`mp:${KEY}`);
      } catch {
        raw = null;
      }
    }
    if (!raw) return;
    const state = JSON.parse(raw) as { savedAt: number; data: unknown };
    // ignore caches older than 30 days
    if (Date.now() - state.savedAt > 30 * 86400_000) return;
    hydrate(qc, state.data as Parameters<typeof hydrate>[1]);
    // Restored data is shown instantly (and offline) but is always revalidated against the server.
    qc.getQueryCache().getAll().forEach((query) => query.invalidate());
  } catch {
    /* corrupt cache: ignore */
  }
}

export function startPersisting(qc: QueryClient): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const save = async () => {
    timer = null;
    try {
      const data = dehydrate(qc, {
        shouldDehydrateQuery: (q) => q.state.status === 'success' && PERSISTED_ROOTS.has(String(q.queryKey[0])),
      });
      const raw = JSON.stringify({ savedAt: Date.now(), data });
      const ok = await idbSet(KEY, raw);
      if (!ok && raw.length < 3_000_000) {
        try {
          localStorage.setItem(`mp:${KEY}`, raw);
        } catch {
          /* quota */
        }
      }
    } catch {
      /* ignore */
    }
  };
  const unsub = qc.getQueryCache().subscribe(() => {
    if (!timer) timer = setTimeout(save, 2000);
  });
  return () => {
    unsub();
    if (timer) clearTimeout(timer);
  };
}

export async function clearCache(): Promise<void> {
  await idbSet(KEY, null);
  try {
    localStorage.removeItem(`mp:${KEY}`);
  } catch {
    /* ignore */
  }
}
